import { lazy, Suspense, useEffect } from "react";
import { Link } from "react-router-dom";
import { FACES } from "~/lib/faces";
import { useSeo } from "~/lib/seo/useSeo";
import { useVisitor } from "~/lib/store";

const Stage = lazy(() =>
  import("~/three/Stage").then((m) => ({ default: m.Stage })),
);

const HINT_TIMEOUT_MS = 8000;

export function Home() {
  useSeo({ pathname: "/" });

  const hintsDismissed = useVisitor((s) => s.hintsDismissed);
  const dismissHints = useVisitor((s) => s.dismissHints);

  useEffect(() => {
    if (hintsDismissed) return;
    const t = window.setTimeout(dismissHints, HINT_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [hintsDismissed, dismissHints]);

  return (
    <section className="flex w-full flex-col items-stretch gap-8 pb-12">
      <div className="relative h-[min(72vh,700px)] min-h-[440px] w-full">
        <Suspense fallback={<CubePlaceholder />}>
          <Stage />
        </Suspense>

        <div
          className={[
            "pointer-events-none absolute inset-x-0 top-6 z-10 px-4 text-center transition-opacity duration-700 sm:top-10",
            hintsDismissed ? "opacity-0" : "opacity-100",
          ].join(" ")}
          aria-hidden={hintsDismissed ? "true" : undefined}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-jg-fg-muted sm:text-xs">
            The JGson Core · drag to rotate · tap a cubie to enter
          </p>
        </div>
      </div>

      <header className="mx-auto w-full max-w-4xl px-4 text-center">
        <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight sm:text-5xl">
          <span className="text-jg-play">Games</span>.{" "}
          <span className="text-jg-engine">Engines</span>.{" "}
          <span className="text-jg-augment">AR</span>.{" "}
          <span className="text-jg-mobile">Mobile</span>.{" "}
          <span className="text-jg-ai">AI</span>.{" "}
          <span className="text-jg-trade">Fintech</span>.
        </h1>
        <p className="mt-3 text-balance text-jg-fg-muted">
          One engineer. Ten years. Eight cubies. Pick one.
        </p>
      </header>

      <ul className="mx-auto grid w-full max-w-6xl grid-cols-2 gap-3 px-4 pt-2 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
        {FACES.map((f) => (
          <li key={f.id}>
            <Link
              to={f.route}
              // h-full so every card stretches to its row's tallest card —
              // without this, Engines (long blurb) is way taller than Trade
              // (short blurb) on mobile's 2-column grid.
              className="block h-full rounded-xl border border-jg-border bg-jg-bg-soft/40 p-4 text-left transition-colors hover:bg-jg-bg-soft"
            >
              <span
                className="font-display text-sm font-semibold"
                style={{ color: f.themeColor }}
              >
                {f.label}
              </span>
              <p className="mt-1 text-xs text-jg-fg-muted">{f.blurb}</p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CubePlaceholder() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 grid place-items-center"
    >
      <div className="size-48 animate-pulse rounded-3xl bg-gradient-to-br from-jg-augment via-jg-play to-jg-trade opacity-60 blur-2xl sm:size-64" />
    </div>
  );
}
